<script>
  // Expandable capture row: a clickable header that reveals the full detail
  // inline (CaptureDetail). Used by /buckets/:id so the per-bucket view shows
  // the same detail as the Captures table without bouncing to /captures/:id.
  // Pass showBucket=false to drop the bucket meta when the surrounding view is
  // already scoped to one.
  import { Link } from 'svelte-routing';
  import MethodChip from './MethodChip.svelte';
  import CaptureDetail from './CaptureDetail.svelte';
  import { api } from '../lib/api.js';
  import { ui } from '../lib/ui.svelte.js';
  import { capturePayload } from '../lib/capture.js';

  let { capture, showBucket = true, onDeleted = () => {} } = $props();

  let open = $state(false);

  function fmt(t) { return new Date(t).toLocaleString(); }
  function toggle() { open = !open; }

  async function copy(e) {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(JSON.stringify(capturePayload(capture), null, 2));
      ui.snack('Capture copied', 'success', 1500);
    } catch (err) { ui.snack('Copy failed: ' + err.message, 'error'); }
  }

  async function del(e) {
    e?.stopPropagation();
    const ok = await ui.ask({
      title: 'Delete capture?',
      message: 'This capture will be permanently removed.',
      confirmText: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try { await api.deleteCapture(capture.id); ui.snack('Capture deleted', 'success'); onDeleted(capture.id); }
    catch (err) { ui.snack(err.message, 'error'); }
  }
</script>

<article>
  <div class="cap-row">
    <button type="button" class="cap-row-main" onclick={toggle} aria-expanded={open}>
      <MethodChip method={capture.method} />
      <div class="flex-1 min-w-0">
        <div class="cap-row-path">
          <Link to={`/captures/${capture.id}`} class="cap-row-path-link" onclick={(e) => e.stopPropagation()}>{capture.path || '/'}</Link>
          {#if capture.query}<span class="cap-row-query">?{capture.query}</span>{/if}
        </div>
        <div class="text-xs text-on-surface-variant mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
          <span>{fmt(capture.created_at)}</span>
          {#if showBucket}<span>bucket: <span class="font-mono">{capture.bucket_id || 'global'}</span></span>{/if}
          {#if capture.subject}<span>subject: <span class="font-mono">{capture.subject}</span></span>{/if}
          <span>{capture.body_size}b{capture.body_truncated ? ' (truncated)' : ''}</span>
          {#if capture.remote_addr}<span>from: <span class="font-mono">{capture.remote_addr}</span></span>{/if}
          {#if capture.proto}<span>http: <span class="font-mono">{capture.proto}</span></span>{/if}
        </div>
      </div>
      <span class="material-symbols-outlined text-on-surface-variant transition-transform cap-row-chevron" style:transform={open ? 'rotate(180deg)' : ''}>
        expand_more
      </span>
    </button>

    <button type="button" class="cap-row-icon" onclick={copy} title="Copy capture JSON" aria-label="Copy capture JSON">
      <span class="material-symbols-outlined text-base">content_copy</span>
    </button>
    <button type="button" class="cap-row-icon cap-row-icon-danger" onclick={del} title="Delete" aria-label="Delete capture">
      <span class="material-symbols-outlined text-base">delete</span>
    </button>
  </div>

  {#if open}
    <CaptureDetail {capture} />
  {/if}
</article>

<style>
  .cap-row {
    display: flex;
    align-items: stretch;
    gap: 0;
  }
  .cap-row-main {
    flex: 1;
    min-inline-size: 0;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 0.5rem 0.75rem 0.875rem;
    background: transparent;
    color: inherit;
    border: 0;
    text-align: start;
    cursor: pointer;
  }
  .cap-row-main:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .cap-row-chevron { align-self: center; }
  .cap-row-path {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 13px;
    word-break: break-all;
  }
  /* The path doubles as a deep link to the standalone detail view, but the
     surrounding button toggles expansion. Stop the event on the link so a
     click navigates instead of expanding. */
  :global(.cap-row-path-link) {
    color: inherit;
    text-decoration: none;
  }
  :global(.cap-row-path-link:hover) {
    color: rgb(var(--md-sys-color-primary));
    text-decoration: underline;
  }
  .cap-row-query {
    color: rgb(var(--md-sys-color-on-surface-variant));
    word-break: break-all;
  }
  .cap-row-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    inline-size: 40px;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    border: 0;
    cursor: pointer;
  }
  .cap-row-icon:hover {
    background: rgb(var(--md-sys-color-on-surface) / 0.06);
    color: rgb(var(--md-sys-color-primary));
  }
  .cap-row-icon-danger:hover {
    color: rgb(var(--md-sys-color-error));
  }
</style>
